import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import PersonIcon from '@mui/icons-material/Person';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

import { CircularProgressIndicator } from '../../../../../shared/components/charts';
import { CohortGuard } from '../../../../../shared/components/CohortGuard';
import { DashboardCard } from '../../../../../shared/components/DashboardCard';
import {
  DateRange,
  DurationSelector,
  FilterDrawer,
  FilterState,
  getAppliedFilterCount,
} from '../../../../../shared/components';
import { AiWellnessPanel, useInsightsChatbotConfig, useStandardInsights } from '../../../insights';
import { useClientScope } from '../../../../../shared/contexts';
import { usePersistedDateRange, usePersistedFilters, useMetricsStats } from '../../../../../shared/hooks';
import PeakieWebLogo from '../../../../../assets/Peakie AI Web Logo.png';

const formatDate = (value: Dayjs) => value.format('YYYY-MM-DD');

export const PredictiveInsights: React.FC = () => {
  const theme = useTheme();
  const { effectiveClientId, isCentralAdmin, isAnonymised } = useClientScope();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { filters, setFilters } = usePersistedFilters();
  const activeFiltersCount = getAppliedFilterCount(filters);
  const { dateRange, setDateRange } = usePersistedDateRange();

  const params = useMemo(() => {
    const start = dateRange[0] ?? dayjs().startOf('month');
    const end = dateRange[1] ?? dayjs().endOf('month');
    return {
      scope_type: 'org' as const,
      start_date: formatDate(start),
      end_date: formatDate(end),
      department: filters.department,
      age_range: filters.ageRange,
      sex: filters.sex?.[0],
      shift_pattern: filters.shiftPattern,
      user_ids: filters.searchUser,
      client_id: effectiveClientId || undefined,
    };
  }, [dateRange, filters, effectiveClientId]);

  const cohortStatsQuery = useMetricsStats({
    metrics: ['active_users'],
    start_date: params.start_date,
    end_date: params.end_date,
    department: params.department,
    age_range: params.age_range,
    sex: params.sex,
    shift_pattern: params.shift_pattern,
    user_ids: params.user_ids,
    client_id: params.client_id !== undefined ? params.client_id || null : undefined,
  });
  const activeUserCount = cohortStatsQuery.data?.data?.active_users?.value;

  const { data, isLoading, error } = useStandardInsights(params);
  const { setConfig: setChatbotConfig } = useInsightsChatbotConfig();

  const insights = data?.data;

  useEffect(() => {
    setChatbotConfig({
      title: 'Peakie AI',
      initialPrompt: 'What stands out this week and what should we do next?',
      buildRequest: (question) => ({
        scope: { type: 'org' },
        question,
        start_date: params.start_date,
        end_date: params.end_date,
        department: params.department,
        age_range: params.age_range,
        sex: params.sex,
        shift_pattern: params.shift_pattern,
        user_ids: params.user_ids,
        client_id: params.client_id,
        k: 8,
      }),
    });
  }, [params, setChatbotConfig]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <DurationSelector value={dateRange} onChange={setDateRange} buttonVariant='outlined' />
            <Button
              variant='text'
              size='medium'
              startIcon={<FilterListIcon />}
              color={activeFiltersCount > 0 ? 'primary' : 'inherit'}
              onClick={() => setIsFilterOpen(true)}
            >
              {activeFiltersCount > 0 ? `FILTER (${activeFiltersCount})` : 'FILTER'}
            </Button>
          </Box>
          <Box component='img' src={PeakieWebLogo} alt='Peakie AI' sx={{ height: { xs: 44, md: 56 }, width: 'auto' }} />
        </Box>

        <FilterDrawer
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApply={(newFilters) => setFilters(newFilters)}
          initialFilters={filters}
        />

        {error && (
          <Alert severity='error'>
            Failed to load insights. {error instanceof Error ? error.message : 'Please try again.'}
          </Alert>
        )}

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {insights && (
          <CohortGuard userCount={activeUserCount} bypass={!isAnonymised} loading={cohortStatsQuery.isLoading}>
            <AiWellnessPanel
              insights={insights}
              scopeKey={`org:${params.client_id ?? 'all'}`}
              scopeLabel='Organisation'
              initialPrompt='What stands out this week and what should we do next?'
              buildRequest={(question) => ({
                scope: { type: 'org' },
                question,
                start_date: params.start_date,
                end_date: params.end_date,
                department: params.department,
                age_range: params.age_range,
                sex: params.sex,
                shift_pattern: params.shift_pattern,
                user_ids: params.user_ids,
                client_id: params.client_id,
                k: 8,
              })}
            />
          </CohortGuard>
        )}
      </Stack>
    </Box>
  );
};
